<template>
  <div class="password-reset-wrapper">
    <div class="container">
      <!-- Header Section -->
      <div class="content-header mb-6">
        <div class="header-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 12l2 2 4-4"/>
            <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
            <path d="M3 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
            <path d="M12 21c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
            <path d="M12 3c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
            <path d="M8 12l1.5-1.5L12 13l4.5-4.5L18 10"/>
          </svg>
        </div>
        <div>
          <h1>Jelszó visszaállítása</h1>
          <p>Állítsa be új jelszavát a fiókja biztonsága érdekében</p>
        </div>
      </div>

      <!-- Main Card -->
      <div class="modern-card card-centered">
        
        <!-- Loading State -->
        <div v-if="loading" class="card-body text-center">
          <div class="loading-content">
            <div class="loading-spinner"></div>
            <p class="mt-4 loading-text">Ellenőrzés folyamatban...</p>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="card-body">
          <div class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--error)" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <h3>Hiba történt</h3>
            <p>{{ error }}</p>
            <button @click="verifyToken" class="modern-btn modern-btn--primary mt-4">
              Újrapróbálás
            </button>
          </div>
        </div>

        <!-- Success State -->
        <div v-else-if="msg_suc" class="card-body text-center">
          <div class="empty-state">
            <div class="card-icon success-icon-large">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
                <path d="M3 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
                <path d="M12 21c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
              </svg>
            </div>
            <h3>Siker!</h3>
            <p>{{ msg_suc }}</p>
          </div>
        </div>

        <!-- Password Reset Form -->
        <div v-else-if="showForm" class="card-body">
          <form @submit.prevent="submitNewPassword" class="form-section">
            
            <!-- New password input -->
            <div class="form-group mb-4">
              <label class="form-label" for="new-password">Új jelszó</label>
              <div class="input-wrapper">
                <input
                  v-model="newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  id="new-password"
                  class="modern-input"
                  :class="{ 'modern-input--error': passwordError }"
                  required
                  placeholder="Írd be az új jelszót"
                />
                <button 
                  type="button" 
                  class="password-toggle"
                  @click="showNewPassword = !showNewPassword"
                >
                  <svg v-if="showNewPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M1 1l22 22"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
              <div v-if="passwordError" class="field-error">
                {{ passwordError }}
              </div>
            </div>

            <!-- Confirm password input -->
            <div class="form-group mb-4">
              <label class="form-label" for="confirm-password">Jelszó megerősítése</label>
              <div class="input-wrapper">
                <input
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  id="confirm-password"
                  class="modern-input"
                  :class="{ 'modern-input--error': confirmPasswordError }"
                  required
                  placeholder="Írd be újra a jelszót"
                />
                <button 
                  type="button" 
                  class="password-toggle"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <svg v-if="showConfirmPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M1 1l22 22"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
              <div v-if="confirmPasswordError" class="field-error">
                {{ confirmPasswordError }}
              </div>
            </div>

            <!-- Password requirements -->
            <div class="password-requirements mb-4">
              <h4>Jelszó követelmények:</h4>
              <ul>
                <li :class="{ 'valid': newPassword.length >= 8 }">
                  <svg v-if="newPassword.length >= 8" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--success)" stroke-width="2">
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                  Legalább 8 karakter
                </li>
                <li :class="{ 'valid': /[A-Z]/.test(newPassword) }">
                  <svg v-if="/[A-Z]/.test(newPassword)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--success)" stroke-width="2">
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                  Nagy betű
                </li>
                <li :class="{ 'valid': /[0-9]/.test(newPassword) }">
                  <svg v-if="/[0-9]/.test(newPassword)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--success)" stroke-width="2">
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                  Szám
                </li>
              </ul>
            </div>

            <!-- Submit button -->
            <button 
              type="submit" 
              class="modern-btn modern-btn--primary w-full"
              :disabled="loading || !isFormValid"
            >
              <span v-if="loading" class="loading-spinner"></span>
              <span v-else>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z"/>
                </svg>
              </span>
              Jelszó mentése
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      token: "",
      newPassword: "",
      confirmPassword: "",
      error: "",
      loading: false,
      showForm: false,
      msg_suc: "",
      showNewPassword: false,
      showConfirmPassword: false,
      passwordError: "",
      confirmPasswordError: ""
    };
  },
  computed: {
    isFormValid() {
      return this.newPassword.length >= 8 && 
             /[A-Z]/.test(this.newPassword) && 
             /[0-9]/.test(this.newPassword) &&
             this.newPassword === this.confirmPassword &&
             this.newPassword.length > 0;
    }
  },
  watch: {
    newPassword() {
      this.validatePasswords();
    },
    confirmPassword() {
      this.validatePasswords();
    }
  },
  created() {
    this.token = this.$route.query.token;
    if (!this.token) {
      this.error = "Hiányzik a token.";
      return;
    }

    this.verifyToken();
  },
  methods: {
    // Verify the token by calling the backend API
    async verifyToken() {
      this.loading = true;
      this.error = "";
      try {
        const response = await axios.get("/api/pwreset", {
          params: { token: this.token },
        });
        if (response.status === 200) {
          this.showForm = true;
        }
      } catch (err) {
        this.error = err.response?.data?.error || "Ismeretlen hiba történt.";
      } finally {
        this.loading = false;
      }
    },

    // Validate passwords in real-time
    validatePasswords() {
      // Reset errors
      this.passwordError = "";
      this.confirmPasswordError = "";

      // Validate new password
      if (this.newPassword.length > 0 && this.newPassword.length < 8) {
        this.passwordError = "A jelszónak legalább 8 karakter hosszúnak kell lennie";
      } else if (this.newPassword.length >= 8 && !/[A-Z]/.test(this.newPassword)) {
        this.passwordError = "A jelszónak tartalmaznia kell legalább egy nagy betűt";
      } else if (this.newPassword.length >= 8 && !/[0-9]/.test(this.newPassword)) {
        this.passwordError = "A jelszónak tartalmaznia kell legalább egy számot";
      }

      // Validate confirm password
      if (this.confirmPassword.length > 0 && this.newPassword !== this.confirmPassword) {
        this.confirmPasswordError = "A jelszavak nem egyeznek";
      }
    },

    // Handle password submission
    async submitNewPassword() {
      // Final validation
      this.validatePasswords();
      
      if (this.newPassword !== this.confirmPassword) {
        this.error = "A két jelszó nem egyezik!";
        return;
      }

      if (!this.isFormValid) {
        this.error = "Kérjük, ellenőrizze a jelszó követelményeket.";
        return;
      }

      // Call the backend API to update the password (send the token and new password)
      this.loading = true;
      this.error = "";
      try {
        const response = await axios.post("/api/reset_password", {
          token: encodeURIComponent(this.token),
          newPassword: this.newPassword,
        });
        if (response.status === 200) {
          this.error = "";
          this.msg_suc = "A jelszó sikeresen megváltozott! Most átirányítjuk a bejelentkezésre.";
          setTimeout(() => {
            this.$router.push('/login');
          }, 2000);
        }
      } catch (err) {
        this.error = err.response?.data?.error || "Ismeretlen hiba történt.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.password-reset-wrapper {
  min-height: 100vh;
  padding: 2rem 0;
  background: var(--color-background);
}

/* Content Header Styles */
.content-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem 2rem;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  margin-bottom: 2rem;
  border: 1px solid var(--color-border);
}

.content-header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-heading);
  margin: 0 0 0.5rem 0;
}

.content-header p {
  color: var(--color-text-muted);
  margin: 0;
  font-size: 1rem;
}

.header-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
}

/* Card Styles */
.modern-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
}

.modern-card:hover {
  box-shadow: var(--shadow-md);
}

.card-body {
  padding: 2rem;
}

/* Form Styles */
.form-section {
  max-width: none;
}

.input-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.password-toggle:hover {
  color: var(--button-primary);
  background: var(--color-background-soft);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--color-heading);
  font-size: 0.95rem;
}

/* Password Requirements */
.password-requirements {
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--color-border);
}

.password-requirements h4 {
  margin: 0 0 1rem 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-heading);
}

.password-requirements ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  transition: all 0.2s ease;
}

.password-requirements li:last-child {
  margin-bottom: 0;
}

.password-requirements li.valid {
  color: var(--success);
}

.password-requirements li svg {
  flex-shrink: 0;
}

/* Field Errors */
.field-error {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--error);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.field-error::before {
  content: "⚠";
  font-size: 1rem;
}

/* Button Styles */
.w-full {
  width: 100%;
}

/* Loading States */
.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
}

.loading-text {
  color: var(--color-text-muted);
  margin-top: 1rem;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border);
  border-radius: 50%;
  border-top-color: var(--button-primary);
  animation: spin 1s ease-in-out infinite;
}

/* Empty State Styles */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-state svg {
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--color-heading);
}

.empty-state p {
  color: var(--color-text-muted);
  margin: 0;
  font-size: 0.95rem;
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Card Styles */
.modern-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
}

.modern-card:hover {
  box-shadow: var(--shadow-md);
}

.card-centered {
  max-width: 500px;
  margin: 0 auto;
}

.card-body {
  padding: 2rem;
}

.success-icon-large {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem auto;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border);
  border-radius: 50%;
  border-top-color: var(--button-primary);
  animation: spin 1s ease-in-out infinite;
}

/* Empty State Styles */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-state svg {
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--color-heading);
}

.empty-state p {
  color: var(--color-text-muted);
  margin: 0;
  font-size: 0.95rem;
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Card Icon for Success State */
.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--success), #059669);
  border-radius: 16px;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-md);
}

/* Responsive Design */
@media (max-width: 768px) {
  .password-reset-wrapper {
    padding: 1rem 0;
  }
  
  .content-header {
    padding: 1.25rem 1.5rem;
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .content-header h1 {
    font-size: 1.75rem;
  }
  
  .header-icon {
    width: 56px;
    height: 56px;
  }
  
  .card-body {
    padding: 1.5rem;
  }
  
  .password-requirements {
    padding: 1rem;
  }
  
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .card-icon {
    width: 64px;
    height: 64px;
  }
}

@media (max-width: 480px) {
  .content-header {
    padding: 1rem 1.25rem;
  }
  
  .content-header h1 {
    font-size: 1.5rem;
  }
  
  .header-icon {
    width: 48px;
    height: 48px;
  }
  
  .card-body {
    padding: 1.25rem;
  }
  
  .password-requirements {
    padding: 0.875rem;
  }
  
  .password-requirements h4 {
    font-size: 0.875rem;
  }
  
  .password-requirements li {
    font-size: 0.8rem;
  }
  
  .empty-state {
    padding: 1.5rem 0.75rem;
  }
  
  .loading-content {
    padding: 1.5rem 0;
  }
  
  .loading-spinner {
    width: 28px;
    height: 28px;
    border-width: 2px;
  }
}
</style>
